import { createFileRoute } from "@tanstack/react-router";
import {
	useState,
	useCallback,
	useEffect,
	type ReactElement,
	type ReactNode,
} from "react";
import { supabase } from "@baseline/web/supabase";

export const Route = createFileRoute("/dev/chat-debug")({
	validateSearch: (search: Record<string, unknown>) => ({
		thread: typeof search.thread === "string" ? search.thread : undefined,
	}),
	component: ChatDebug,
});

interface ThreadData {
	id: string;
	title: string | null;
	model: string | null;
	system_prompt: string | null;
	initial_context: string | null;
	page_context: Record<string, unknown> | null;
	messages: Array<{
		id?: string;
		role: string;
		parts?: Array<Record<string, unknown>>;
		content?: string;
		[key: string]: unknown;
	}>;
	prompt_tokens: number;
	completion_tokens: number;
	total_tokens: number;
	created_at: string;
	updated_at: string;
}

function ChatDebug(): ReactElement {
	const { thread: threadParam } = Route.useSearch();
	const [threadId, setThreadId] = useState(threadParam ?? "");
	const [thread, setThread] = useState<ThreadData | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [autoLoaded, setAutoLoaded] = useState(false);

	const loadThread = useCallback(async (id: string) => {
		if (!id.trim()) return;
		setLoading(true);
		setError(null);
		setThread(null);

		const { data, error: fetchError } = await supabase
			.from("chat_threads")
			.select(
				"id, title, model, system_prompt, initial_context, page_context, messages, prompt_tokens, completion_tokens, total_tokens, created_at, updated_at",
			)
			.eq("id", id.trim())
			.single();

		setLoading(false);

		if (fetchError || !data) {
			setError(fetchError?.message ?? "Thread not found");
			return;
		}

		setThread(data as unknown as ThreadData);
	}, []);

	// Auto-load when ?thread= is in the URL
	useEffect(() => {
		if (threadParam && !autoLoaded) {
			setAutoLoaded(true);
			loadThread(threadParam);
		}
	}, [threadParam, autoLoaded, loadThread]);

	const handleSubmit = useCallback(
		(e: React.FormEvent) => {
			e.preventDefault();
			loadThread(threadId);
		},
		[threadId, loadThread],
	);

	return (
		<div className="mx-auto max-w-4xl px-6 py-8">
			<h1 className="text-fg mb-1 text-xl font-semibold">
				Chat Thread Debug
			</h1>
			<p className="text-fg-ghost mb-6 text-sm">
				Inspect what the LLM sees: system prompt, model, messages, and
				tool calls.
			</p>

			<form
				onSubmit={handleSubmit}
				className="mb-8 flex items-center gap-2"
			>
				<input
					type="text"
					value={threadId}
					onChange={(e) => setThreadId(e.target.value)}
					placeholder="Thread UUID"
					className="border-line bg-surface text-fg placeholder:text-fg-ghost focus:border-accent flex-1 rounded border px-3 py-1.5 font-mono text-sm outline-none"
					data-testid="thread-id-input"
				/>
				<button
					type="submit"
					disabled={loading || !threadId.trim()}
					className="bg-accent text-surface rounded px-4 py-1.5 text-sm font-medium disabled:opacity-50"
					data-testid="load-thread-btn"
				>
					{loading ? "Loading..." : "Load"}
				</button>
			</form>

			{error && (
				<div className="mb-6 rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
					{error}
				</div>
			)}

			{thread && <ThreadView thread={thread} />}
		</div>
	);
}

function ThreadView({ thread }: { thread: ThreadData }): ReactElement {
	const messages = thread.messages ?? [];

	return (
		<div className="flex flex-col gap-6">
			{/* Meta */}
			<Section title="Thread Info">
				<MetaRow label="ID" value={thread.id} mono />
				<MetaRow label="Title" value={thread.title ?? "(none)"} />
				<MetaRow label="Model" value={thread.model ?? "(not set)"} />
				<MetaRow label="Created" value={thread.created_at} />
				<MetaRow label="Updated" value={thread.updated_at} />
				<MetaRow
					label="Tokens"
					value={`${thread.total_tokens.toLocaleString()} total (${thread.prompt_tokens.toLocaleString()} prompt + ${thread.completion_tokens.toLocaleString()} completion)`}
				/>
				{thread.page_context && (
					<MetaRow
						label="Page Context"
						value={JSON.stringify(thread.page_context, null, 2)}
						pre
					/>
				)}
			</Section>

			{/* System Prompt */}
			<Section title="System Prompt">
				{thread.system_prompt ? (
					<pre className="text-fg-secondary text-xs leading-relaxed whitespace-pre-wrap">
						{thread.system_prompt}
					</pre>
				) : (
					<p className="text-fg-ghost text-sm italic">
						Not frozen yet (no messages sent)
					</p>
				)}
			</Section>

			{/* Initial Context */}
			<Section title="Initial Context (injected as user message #0)">
				{thread.initial_context ? (
					<pre className="text-fg-secondary text-xs leading-relaxed whitespace-pre-wrap">
						{thread.initial_context}
					</pre>
				) : (
					<p className="text-fg-ghost text-sm italic">
						No context (home page or not frozen yet)
					</p>
				)}
			</Section>

			{/* Messages */}
			<Section title={`Messages (${messages.length})`}>
				{messages.length === 0 ? (
					<p className="text-fg-ghost text-sm italic">
						No messages in this thread
					</p>
				) : (
					<div className="flex flex-col gap-4">
						{messages.map((msg, i) => (
							<MessageView
								key={msg.id ?? i}
								index={i}
								msg={msg}
							/>
						))}
					</div>
				)}
			</Section>
		</div>
	);
}

function Section({
	title,
	children,
}: {
	title: string;
	children: ReactNode;
}): ReactElement {
	return (
		<div className="border-line rounded-lg border">
			<div className="border-line border-b px-4 py-2">
				<h2 className="text-fg text-sm font-semibold">{title}</h2>
			</div>
			<div className="px-4 py-3">{children}</div>
		</div>
	);
}

function MetaRow({
	label,
	value,
	mono,
	pre,
}: {
	label: string;
	value: string;
	mono?: boolean;
	pre?: boolean;
}): ReactElement {
	return (
		<div className="flex gap-3 py-0.5 text-xs">
			<span className="text-fg-ghost w-28 shrink-0 font-medium">
				{label}
			</span>
			{pre ? (
				<pre className="text-fg-secondary min-w-0 flex-1 font-mono whitespace-pre-wrap">
					{value}
				</pre>
			) : (
				<span
					className={`text-fg-secondary min-w-0 flex-1 ${mono ? "font-mono" : ""}`}
				>
					{value}
				</span>
			)}
		</div>
	);
}

const ROLE_STYLES: Record<string, string> = {
	user: "bg-accent text-surface",
	assistant: "bg-surface-raised text-fg",
	system: "bg-yellow-100 text-yellow-900 dark:bg-yellow-950 dark:text-yellow-200",
	tool: "bg-purple-100 text-purple-900 dark:bg-purple-950 dark:text-purple-200",
};

function MessageView({
	index,
	msg,
}: {
	index: number;
	msg: ThreadData["messages"][number];
}): ReactElement {
	const role = msg.role;
	const parts = msg.parts ?? [];
	const badgeStyle = ROLE_STYLES[role] ?? "bg-surface-raised text-fg";

	return (
		<div className="border-line rounded border">
			<div className="border-line flex items-center gap-2 border-b px-3 py-1.5">
				<span className="text-fg-ghost font-mono text-[11px]">
					#{index}
				</span>
				<span
					className={`rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase ${badgeStyle}`}
				>
					{role}
				</span>
				{msg.id && (
					<span className="text-fg-ghost truncate font-mono text-[10px]">
						{msg.id}
					</span>
				)}
			</div>
			<div className="px-3 py-2">
				{/* Simple string content (model format) */}
				{typeof msg.content === "string" && msg.content && (
					<pre className="text-fg-secondary text-xs leading-relaxed whitespace-pre-wrap">
						{msg.content}
					</pre>
				)}

				{/* Parts-based content (UIMessage format) */}
				{parts.length > 0 && (
					<div className="flex flex-col gap-2">
						{parts.map((part, pi) => (
							<PartView key={pi} part={part} />
						))}
					</div>
				)}

				{/* Fallback: no content */}
				{!msg.content && parts.length === 0 && (
					<span className="text-fg-ghost text-xs italic">
						(empty)
					</span>
				)}
			</div>
		</div>
	);
}

function PartView({ part }: { part: Record<string, unknown> }): ReactElement {
	const type = String(part.type ?? "unknown");

	// Text part
	if (type === "text") {
		return (
			<pre className="text-fg-secondary text-xs leading-relaxed whitespace-pre-wrap">
				{String(part.text ?? "")}
			</pre>
		);
	}

	// Tool call / dynamic tool / tool-{name} parts
	const isToolPart =
		type === "dynamic-tool" ||
		(type.startsWith("tool-") && type !== "tool-result");

	if (isToolPart) {
		const toolName =
			(part.toolName as string) ??
			(type.startsWith("tool-") ? type.slice(5) : "unknown");
		const input = part.input ?? part.args ?? {};
		const output = part.output;
		const state = part.state as string | undefined;

		return (
			<div className="border-line rounded border">
				<div className="flex items-center gap-2 bg-purple-50 px-2 py-1 dark:bg-purple-950">
					<span className="text-[11px] font-semibold text-purple-700 dark:text-purple-300">
						TOOL CALL
					</span>
					<span className="font-mono text-xs font-medium text-purple-900 dark:text-purple-100">
						{toolName}
					</span>
					{state && (
						<span className="text-fg-ghost ml-auto text-[10px]">
							{state}
						</span>
					)}
				</div>
				<div className="px-2 py-1.5">
					<div className="text-fg-ghost mb-1 text-[10px] font-semibold uppercase">
						Input
					</div>
					<pre className="text-fg-secondary mb-2 text-[11px] whitespace-pre-wrap">
						{JSON.stringify(input, null, 2)}
					</pre>
					{output !== undefined && (
						<>
							<div className="text-fg-ghost mb-1 text-[10px] font-semibold uppercase">
								Output
							</div>
							<pre className="text-fg-secondary max-h-60 overflow-auto text-[11px] whitespace-pre-wrap">
								{JSON.stringify(output, null, 2)}
							</pre>
						</>
					)}
				</div>
			</div>
		);
	}

	// Tool result part
	if (type === "tool-result") {
		return (
			<div className="border-line rounded border">
				<div className="bg-green-50 px-2 py-1 dark:bg-green-950">
					<span className="text-[11px] font-semibold text-green-700 dark:text-green-300">
						TOOL RESULT
					</span>
					{typeof part.toolName === "string" && (
						<span className="ml-2 font-mono text-xs text-green-900 dark:text-green-100">
							{part.toolName}
						</span>
					)}
				</div>
				<pre className="text-fg-secondary max-h-60 overflow-auto px-2 py-1.5 text-[11px] whitespace-pre-wrap">
					{JSON.stringify(
						part.result ?? part.output ?? part,
						null,
						2,
					)}
				</pre>
			</div>
		);
	}

	// Unknown part type — dump as JSON
	return (
		<div className="border-line rounded border px-2 py-1.5">
			<span className="text-fg-ghost mb-1 block text-[10px] font-semibold uppercase">
				{type}
			</span>
			<pre className="text-fg-secondary text-[11px] whitespace-pre-wrap">
				{JSON.stringify(part, null, 2)}
			</pre>
		</div>
	);
}
